/**
 * Helpers to resolve scoped sale status fields from POS API responses.
 * Creditech exposes approval_status and settlement_status; other tenants may only expose status.
 */

import { getStatusLabel, getStatusColors } from '../config/saleStatusConfig';

export function getCompanyName(rawName = process.env.REACT_APP_COMPANY_NAME) {
  return String(rawName || '').replace(/['"]/g, '').trim();
}

export function isCreditechTenant(companyName = getCompanyName()) {
  return getCompanyName(companyName).toLowerCase() === 'creditech';
}

/**
 * Creditech POS does not offer Pagada in the settlement filter (rows still show it).
 * Other tenants keep the paid option so the table and filter stay aligned.
 */
export function filterSettlementStatusOptions(options = [], companyName = getCompanyName()) {
  if (!Array.isArray(options)) {
    return [];
  }

  if (!isCreditechTenant(companyName)) {
    return options;
  }

  return options.filter((option) => option.value !== 'paid');
}

/**
 * @typedef {{ value: string, label: string }} SaleScopedStatus
 */

/** @deprecated Legacy settlement values mapped to current POS presentation. */
const SETTLEMENT_PRESENTATION_ALIASES = {
  observed: 'invoice_required',
};

/**
 * Normalize status to { value, label } from various API shapes.
 * @param {object|string|null|undefined} statusOrObj
 * @returns {SaleScopedStatus}
 */
export function normalizeScopedStatus(statusOrObj) {
  if (!statusOrObj) {
    return { value: '', label: '' };
  }

  if (typeof statusOrObj === 'string') {
    return { value: statusOrObj, label: getStatusLabel(statusOrObj) };
  }

  const value = statusOrObj.value ?? statusOrObj;
  const label = statusOrObj.label ?? getStatusLabel(value);

  return { value: String(value), label: String(label) };
}

/**
 * Maps legacy settlement values to their POS presentation (e.g. observed → invoice_required).
 * @param {object|string|null|undefined} statusOrObj
 * @returns {SaleScopedStatus}
 */
export function normalizeSettlementPresentation(statusOrObj) {
  const normalized = normalizeScopedStatus(statusOrObj);
  const alias = SETTLEMENT_PRESENTATION_ALIASES[normalized.value];

  if (!alias) {
    return normalized;
  }

  return {
    value: alias,
    label: getStatusLabel(alias),
  };
}

/**
 * @param {object|null|undefined} sale
 * @returns {SaleScopedStatus}
 */
export function getApprovalStatus(sale) {
  if (!sale) {
    return { value: '', label: '' };
  }

  if (sale.approval_status) {
    return normalizeScopedStatus(sale.approval_status);
  }

  if (sale.sale_status) {
    return normalizeScopedStatus(sale.sale_status);
  }

  return normalizeScopedStatus(sale.status);
}

/**
 * @param {object|null|undefined} sale
 * @returns {SaleScopedStatus|null}
 */
export function getSettlementStatus(sale) {
  if (!sale || sale.settlement_status == null) {
    return null;
  }

  return normalizeSettlementPresentation(sale.settlement_status);
}

/**
 * @param {object|null|undefined} sale
 * @returns {string}
 */
export function getApprovalStatusLabel(sale) {
  return getApprovalStatus(sale).label;
}

/**
 * @param {object|null|undefined} sale
 * @returns {string}
 */
export function getSettlementStatusLabel(sale) {
  const settlement = getSettlementStatus(sale);
  return settlement?.label ?? '—';
}

/**
 * @param {string} value
 * @returns {string}
 */
export function getScopedStatusBadgeClass(value) {
  return value ? `sale-status-${value}` : 'sale-status-default';
}

/**
 * @param {object|null|undefined} sale
 * @returns {string}
 */
export function getApprovalStatusClass(sale) {
  return getScopedStatusBadgeClass(getApprovalStatus(sale).value);
}

/**
 * @param {object|null|undefined} sale
 * @returns {string}
 */
export function getSettlementStatusClass(sale) {
  const settlement = getSettlementStatus(sale);
  return getScopedStatusBadgeClass(settlement?.value ?? '');
}

/**
 * @param {string} value
 * @returns {object}
 */
export function getScopedStatusColors(value) {
  return getStatusColors(value);
}

/**
 * @param {object|null|undefined} sale
 * @returns {object}
 */
export function getApprovalStatusColors(sale) {
  return getScopedStatusColors(getApprovalStatus(sale).value);
}

/**
 * @param {object|null|undefined} sale
 * @returns {object}
 */
export function getSettlementStatusColors(sale) {
  const settlement = getSettlementStatus(sale);
  return getScopedStatusColors(settlement?.value ?? '');
}

/**
 * Compact dual-status row for mobile cards.
 * @param {object|null|undefined} sale
 * @returns {{ approval: SaleScopedStatus, settlementLabel: string, hasSettlement: boolean }}
 */
export function getSaleScopedStatusSummary(sale) {
  return {
    approval: getApprovalStatus(sale),
    settlementLabel: getSettlementStatusLabel(sale),
    hasSettlement: getSettlementStatus(sale) !== null,
  };
}
